import type { ErrorEvent, EventHint } from '@sentry/nextjs';
import {
  getCurrentScope,
  getGlobalScope,
  getIsolationScope,
  init,
} from '@sentry/nextjs';

import type {
  DropReason,
  IgnoredDropHandler,
  IgnoredDropInfo,
} from './drop-telemetry';
import {
  buildDropInfo,
  DROP_REASONS,
  isLocalOrigin,
  normalizeRoutePath,
  resolveEventText,
} from './drop-telemetry';
import { isPreviewEnvironment } from './preview-environment';
import type {
  ProcessorDropHandler,
  ProcessorDropInfo,
  SinkArmedHandler,
  SinkArmedInfo,
} from './processor-drop-telemetry';
import { instrumentEventProcessorDrops } from './processor-drop-telemetry';

type Parameters<T extends (args: never) => unknown> = T extends (
  ...args: infer P
) => unknown
  ? P
  : never;

/**
 * Resolve the Sentry environment tag from Vercel-injected env vars first,
 * falling back to NODE_ENV for local / non-Vercel hosts.
 *
 * On the browser, only NEXT_PUBLIC_* env vars are available — we expose
 * VERCEL_ENV via NEXT_PUBLIC_VERCEL_ENV in next.config.mjs (or via the
 * Vercel system env-var mirror) so previews are tagged correctly.
 *
 * VERCEL_ENV values: 'production' | 'preview' | 'development'
 */
function resolveEnvironment(): string {
  return (
    process.env.NEXT_PUBLIC_VERCEL_ENV ??
    process.env.VERCEL_ENV ??
    process.env.NODE_ENV ??
    'unknown'
  );
}

/**
 * Detect ZodErrors thrown from getSupabaseClientKeys (the most common
 * symptom of a misconfigured preview without NEXT_PUBLIC_SUPABASE_*).
 */
function isMissingSupabaseKeysError(
  error: unknown,
  eventErrorValue: string,
): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : '';
  const combined = `${message}\n${eventErrorValue}`.toLowerCase();
  return (
    combined.includes('next_public_supabase_url') ||
    combined.includes('next_public_supabase_public_key') ||
    combined.includes('next_public_supabase_anon_key')
  );
}

/**
 * Detect the downstream TypeError thrown by Next.js's app-page runtime
 * when it tries to mutate `.message` on the frozen ZodError that
 * getSupabaseClientKeys throws. Same root cause as BAL-169 but a
 * separate Sentry issue (BAL-171 / BALLEE-BV). The crash itself fires
 * server-side, but we mirror the filter here for defense-in-depth in
 * case an error boundary replays it on the client.
 */
function isPreviewSupabaseKeysTypeError(
  event: ErrorEvent,
  eventErrorValue: string,
): boolean {
  const exception = event.exception?.values?.[0];
  if (!exception) return false;
  if (exception.type !== 'TypeError') return false;
  if (!/cannot set property message of/i.test(eventErrorValue)) return false;
  const frames = exception.stacktrace?.frames ?? [];
  return frames.some((frame) => {
    const fn = frame.function ?? '';
    const file = frame.filename ?? '';
    return (
      fn.includes('getSupabaseClientKeys') ||
      file.includes('get-supabase-client-keys')
    );
  });
}

/**
 * rrweb's DOM serializer, bundled by BOTH session recorders this app loads,
 * warns when it cannot inline a `<style>` element's CSS because the stylesheet
 * is cross-origin. The upstream template is verbatim:
 *
 *   catch (err) {
 *     console.warn(`Cannot get CSS styles from text's parentNode. Error: ${err}`, textNode);
 *   }
 *
 * It is a CATCH block that then continues with the element's original
 * textContent, so the recorder recovers on its own — the only cost is that one
 * cross-origin sheet is not inlined into the replay. Nothing on the page is
 * broken, which matches the production signal: BALLEE-HG is 7 events / 0 users
 * affected on /:handle, Firefox Mobile on Android, the warning repeating within
 * a single pageload as the recorder re-serializes the DOM.
 *
 * This is a closed observability loop, not a product defect. We register BOTH
 * `Sentry.replayIntegration()` — whose @sentry/replay build ships this exact
 * string in its executable ESM and CJS, not merely in a sourcemap — AND
 * `captureConsoleIntegration({ levels: ['error', 'warn'] })`, in
 * apps/web/instrumentation-client.ts. Our recorder warns; our own console
 * integration captures that warning and reports it back to Sentry as an issue.
 * posthog-js bundles the same rrweb code and can emit it too. The sentence
 * appears NOWHERE in apps/ or packages/ — verified by grep, so it can only ever
 * be third-party recorder output.
 *
 * WHY A STRUCTURAL GUARD AND NOT AN `IGNORED_ERROR_PATTERNS` ENTRY: an entry
 * there would be dead code for this class. Verified against @sentry/core@10.70.0
 * — `console.warn(string, textNode)` has no argument that is `instanceof Error`,
 * so `captureConsoleIntegration` takes its `captureMessage` branch, which builds
 * the hint as `{ event_id, ...{ captureContext, syntheticException } }` and
 * NEVER sets `originalException`. `addExceptionMechanism` then no-ops because
 * there is no `event.exception`, so no `mechanism` is stamped either. The text
 * survives ONLY on `event.message`, while the ignore-list below is matched
 * against `hint.originalException` and `event.exception[0].value` — both empty
 * here. The production event confirms the shape: type `default`, no exception
 * entry, tags `logger=console` and `level=warning`.
 *
 * The guard is four conjunctive structural conditions, none of which a genuine
 * application failure satisfies:
 *   1. `logger === 'console'` — stamped unconditionally by captureConsole; a
 *      thrown application error never carries it.
 *   2. `level === 'warning'`  — console.warn only. An error-level capture of the
 *      same text is KEPT.
 *   3. no `event.exception`   — a real failure carries one, so this can never be
 *      a swallowed crash.
 *   4. the message STARTS with the verbatim upstream sentence.
 *
 * Deliberately narrower than a bare message match: it cannot swallow a
 * Postgres/Supabase-outage symptom (those are exceptions, and the
 * cloudflare-origin-timeout block in beforeSend KEEPS them before this runs),
 * and it cannot swallow a first-party error that merely quotes the sentence.
 * See BALLEE-HG.
 */
const RRWEB_CSS_SERIALIZER_WARNING =
  /^cannot get css styles from text's parentnode\. error:/i;

function isRrwebCssSerializerWarning(event: ErrorEvent): boolean {
  if (event.logger !== 'console') return false;
  if (event.level !== 'warning') return false;
  if (event.exception?.values?.length) return false;
  const message = typeof event.message === 'string' ? event.message : '';
  return RRWEB_CSS_SERIALIZER_WARNING.test(message);
}

// Error patterns to completely ignore (transient/expected errors)
//
// IMPORTANT: do NOT add patterns matching Postgres-outage symptoms here.
// Outage symptom classes (forbidden in this list) include: upstream fetch
// failures, dropped/reset/closed connections, pool-saturation messages,
// reserved-slot warnings, Postgres temporarily-unavailable signals, and
// excessive-connection errors. The browser sees these as Supabase REST
// calls returning HTML 5xx bodies — silencing them hides Supabase Postgres
// outages from Sentry. See
// `docs/architecture/resilience-to-supabase-outages.md` (Layer 5).
// Enforcement: critical-rules-supabase-outage-resilience clause.
const IGNORED_ERROR_PATTERNS = [
  // Transient user-network errors (browser-side noise, NOT outage signals)
  /load failed/i,
  /network request failed/i,
  /network error/i,
  /abort/i,
  // Firefox's dialect of the same benign fetch-abort class above. Firefox
  // surfaces an interrupted response-body stream (e.g. an in-flight Next.js
  // RSC prefetch cancelled when the user navigates away) as
  // `TypeError: Error in input stream` — the nsIInputStream wording — instead
  // of Chrome's "aborted" / Safari's "Load failed". It slipped through because
  // none of the patterns above match that exact phrasing (BALLEE-D2 / BAL-656).
  // Confirmed browser noise, not a defect: Firefox-only across 2 OSes and 2
  // unrelated routes (/home/artist/profile/setup, /:handle), no app stack
  // frames, caught by the app error boundary, preceded by aborted `_rsc=`
  // prefetch fetches. This is NOT a Supabase-outage symptom — an outage is
  // cross-browser and returns an HTML 5xx body, which the
  // cloudflare-origin-timeout fingerprint above catches and KEEPS before this
  // ignore-list runs. Same BAL-355 benign-browser-noise direction.
  /error in input stream/i,
  // Browser-specific transient errors
  /could not load "util"/i,
  /resizeobserver loop/i,
  /script error/i,
  /non-error promise rejection/i,
  /object not found/i,
  // React hydration errors (often transient)
  /hydration failed/i,
  /rendered more hooks/i,
  /rendered fewer hooks/i,
  // DOM manipulation errors (usually browser extensions interfering)
  /removeChild.*not a child/i,
  /insertBefore.*not a child/i,
  /the node to be removed is not a child/i,
  /the node before which.*is not a child/i,
  /cannot read properties of null.*removeChild/i,
  /cannot read properties of null.*insertBefore/i,
  // Browser-extension / injected-script noise: `domainName` appears NOWHERE in
  // our source (verified). Single-event, zero-users TypeErrors on /:handle where
  // an injected script reads `e.domainName` on a null object — minified frame
  // attributed to the page URL (app:///<handle>), not a real file. Covers both
  // WebKit ("null is not an object") and Chrome ("cannot read properties") (BAL-273).
  /null is not an object.*\.domainName/i,
  /cannot read propert.*null.*\bdomainName\b/i,
  // Maximum call stack errors (often infinite loop in extensions or transient)
  /maximum call stack size exceeded/i,
  // QueryClient hydration errors (transient during SSR/hydration)
  /no queryclient set/i,
  /use queryclientprovider/i,
  // Expected validation errors (business logic)
  /dancer is already cast/i,
  /already assigned/i,
  /scheduling conflict/i,
  // Auth validation errors (expected user input errors)
  /new password should be different from the old password/i,
  /password is too weak/i,
  // GoTrue's AuthWeakPasswordError message ("Password should be at least N
  // characters."). The /password is too weak/ pattern above does NOT match
  // this server-generated wording, so weak-password rejections on
  // /auth/sign-up slipped through as unhandled-rejection noise
  // (BALLEE-F7 / BALLEE-F9, BAL-576). \d+ keeps it robust to the configured
  // minimum length. Also guarded by name below for defense-in-depth.
  /password should be at least \d+ character/i,
  /user already registered/i,
  /email already in use/i,
  // Rate limiting (expected security feature)
  /rate limit/i,
  /you can only request this after/i,
  /too many requests/i,
  /email rate limit exceeded/i,
  // Supabase Auth rate limiting
  /for security purposes, you can only request this after/i,
  // RLS access denied in admin context (stale session, UI handles gracefully)
  /access denied by security policy/i,
  // Tesseract.js OCR worker error — expected when image can't be decoded (HEIC, corrupt files, etc.)
  // Worker onerror fires independently of the Promise chain, bypassing the try-catch in use-ocr-extraction.ts
  /error attempting to read image/i,
  // Vue.js / Vue Router noise from third-party widgets and webview-injected SDKs
  // (Instagram/Facebook/TikTok in-app browsers, affiliate scripts). We are React/Next.js;
  // any stack frame from compiled Vue is foreign code we cannot fix.
  /avoided redundant navigation to current location/i,
  /cannot read properties of undefined \(reading 'styleClasses'\)/i,
  // IAB (Interactive Advertising Bureau) in-app browser SDK noise — fires from
  // Android/iOS webviews when users open Ballee links from social-media ads.
  /error invoking enabledidusertypeonkeyboardlogging/i,
  /java object is gone/i,
  // IDBDatabase transient — fires when the user closes a tab mid-operation or a
  // browser extension tears down its IndexedDB during page navigation.
  /idbdatabase.*the database connection is closing/i,
  /the database connection is closing/i,
  // Turbopack chunk-load failure after a deploy: a cached page asks for a
  // chunk hash the new release no longer serves. Turbopack's runtime phrases
  // it `Failed to load chunk <path> from module <id>` — NOT webpack's
  // `Loading chunk N failed` / `ChunkLoadError`, which is all Sentry's
  // org-level `chunk-load-error` inbound filter keys on. With that filter ON,
  // two console-message events (no exception, so no type to match) still
  // landed on / and flow.ballee.co/ (BALLEE-HC / BALLEE-H7, BAL-979 / BAL-980).
  // `apps/web/components/chunk-error-handler.tsx` already auto-reloads this
  // class, so the only user impact is one reload. Anchored on the full
  // Turbopack sentence (`from module`) so it cannot swallow `Failed to fetch`
  // or any other load failure. Dropping it here, rather than relying on the
  // inbound filter, is what records it in the BAL-856 drop telemetry under a
  // stable pattern slug.
  /failed to load chunk .* from module \d+/i,
  // @supabase/auth-js `lib/locks.js` (navigatorLock) console.warn on browsers
  // whose `navigator.locks.request` returns a null lock (Mobile Safari 18.5 /
  // iOS on BALLEE-HE). The SDK logs it and then runs the callback anyway —
  // "pretend the lock is acquired ... and just run the function" — so auth
  // is unaffected. Sibling of BALLEE-BT ("Acquiring an exclusive Navigator
  // LockManager lock ... immediately failed", ignored 2026-05-10 as a
  // multi-tab token-lock race the SDK recovers from). The tails are the real
  // anchors, not the `Navigator LockManager` prefix: the same helper also
  // says `returned null lock even with steal: true` (not covered, no
  // evidence yet), and our own `packages/supabase/src/clients/browser-client.ts`
  // throws `Acquiring an exclusive Navigator LockManager lock "<name>" was
  // aborted: page lifecycle state changed` (BAL-792). Neither pattern below
  // matches that first-party one — note it is already caught by the bare
  // `/abort/i` above, a pre-existing client-only gap the server list closed
  // under BAL-891 — so do not loosen these tails (BAL-982).
  /navigator lockmanager returned a null lock/i,
  /acquiring an exclusive navigator lockmanager lock .* immediately failed/i,
];

// External script paths to filter (browser extensions, third-party widgets)
const EXTERNAL_SCRIPT_PATHS = [
  'api_utils.js',
  'chrome-extension://',
  'moz-extension://',
  // MetaMask browser extension injects this script into every page
  'app:///scripts/inpage.js',
  // IAB in-app browser SDK (Instagram/Facebook/TikTok webviews). The scheme is
  // injected by the host app, never by us — anything from this origin is foreign.
  'iabjs://',
];

/**
 * Keys a structured logger uses for the human-readable line. `msg` is pino's;
 * `message` covers the other common shape.
 */
const STRUCTURED_LOG_MESSAGE_KEYS = ['msg', 'message'] as const;

/**
 * Cap on a recovered message. `Error.message` is unbounded and the recovered
 * text is arbitrary upstream log content, so bound what we write back onto the
 * event rather than letting one pathological log line define an issue title.
 */
const MAX_RECOVERED_MESSAGE_LENGTH = 500;

function extractStructuredLogMessage(value: unknown): string | null {
  if (!value || typeof value !== 'object') return null;
  for (const key of STRUCTURED_LOG_MESSAGE_KEYS) {
    const candidate = (value as Record<string, unknown>)[key];
    if (typeof candidate === 'string' && candidate.trim() !== '') {
      return candidate;
    }
  }
  return null;
}

/**
 * Recover a readable message from a console capture whose object arguments
 * were String()-cast to `[object Object]`.
 *
 * WHY this exists (BALLEE-GZ). `captureConsoleIntegration` joins console
 * arguments with `String()`, so a single object argument becomes the literal
 * `[object Object]` in BOTH `event.message` and `hint.originalException`
 * (@sentry/core 10.70.0: `safeJoin` → `stringifyValue` → `[object ${name}]`).
 * Our browser logger is pino configured with `browser: { asObject: true }`
 * (`packages/logger/src/impl/pino.ts`), so every client-side `logger.error()`
 * is literally `console.error({ level, msg, time })` — one object argument.
 * The diagnostic text then survives ONLY under `event.extra.arguments`.
 *
 * The cost is not cosmetic. Every distinct object-argument `console.error`
 * collapses into ONE Sentry fingerprint, and no message-matching filter can
 * see the real text — neither `IGNORED_ERROR_PATTERNS` below nor Sentry's
 * ORG-LEVEL inbound filters. That is how a Turbopack chunk-load error, a class
 * already filtered org-wide by its string form (`chunk-load-error`, and
 * separately auto-reloaded by `apps/web/components/chunk-error-handler.tsx`),
 * still leaked through on /:handle as an unfiltered `[object Object]` issue.
 *
 * Deliberately conservative: it returns null unless an OBJECT argument
 * actually yielded a message. Without that guard `console.error('Prefixed:',
 * {code: 7})` would be rewritten from `Prefixed: [object Object]` to the
 * strictly less informative `Prefixed:`. Never widening the message, only
 * recovering text that would otherwise be unreachable.
 *
 * Recovery alone is not enough for the chunk-load class. The first version of
 * this note relied on Sentry's org-level `chunk-load-error` inbound filter to
 * drop the recovered text; BALLEE-HC / BALLEE-H7 falsified that. The filter
 * keys on webpack's `Loading chunk N failed` wording and the `ChunkLoadError`
 * exception type, and a Turbopack console capture carries neither. The
 * Turbopack sentence is therefore in `IGNORED_ERROR_PATTERNS` above, where the
 * BAL-856 drop telemetry records each drop under a stable pattern slug.
 */
export function recoverConsoleObjectMessage(event: ErrorEvent): string | null {
  const message = event.message;
  // `[object ` also covers `[object Array]`, `[object Null]`, etc.
  if (typeof message !== 'string' || !message.includes('[object ')) {
    return null;
  }

  const args = (event.extra as { arguments?: unknown } | undefined)?.arguments;
  if (!Array.isArray(args)) return null;

  const parts: string[] = [];
  let recoveredFromObject = false;

  for (const arg of args) {
    if (typeof arg === 'string') {
      if (arg.trim() !== '') parts.push(arg);
      continue;
    }
    const extracted = extractStructuredLogMessage(arg);
    if (extracted) {
      parts.push(extracted);
      recoveredFromObject = true;
    }
  }

  if (!recoveredFromObject) return null;

  const joined = parts.join(' ');
  if (joined === message) return null;

  return joined.length > MAX_RECOVERED_MESSAGE_LENGTH
    ? joined.slice(0, MAX_RECOVERED_MESSAGE_LENGTH)
    : joined;
}

// Return the first matching ignore pattern's source (a stable slug for
// telemetry), or null when the error should be kept.
function findIgnoredPattern(errorValue: string): string | null {
  for (const pattern of IGNORED_ERROR_PATTERNS) {
    if (pattern.test(errorValue)) {
      return pattern.source;
    }
  }
  return null;
}

/**
 * Telemetry hook for events discarded by IGNORED_ERROR_PATTERNS (BAL-856).
 * beforeSend returns null client-side, so without this hook a drop leaves
 * zero trace anywhere — a 496-event burst on 2026-07-29 and a 577-event
 * burst on 2026-08-09/10 were only visible as an aggregate discard counter.
 * The consumer (apps/web/instrumentation-client.ts) forwards drops to
 * PostHog so per-pattern per-day counts are queryable and alertable.
 *
 * Shape and sanitization live in `drop-telemetry.ts`: since BAL-891 the
 * browser, Node and Edge halves all write the SAME `sentry_event_dropped`
 * event, so they must redact identically or the table is safe on some rows
 * and raw on others.
 */
export type { IgnoredDropInfo, IgnoredDropHandler };

/**
 * BAL-959: the `event_processor` half of the same story. `onIgnoredDrop` above
 * covers only `beforeSend` drops, which Sentry counts under a DIFFERENT
 * `client_discard` reason — so five bursts counted as `event_processor`
 * (peaking at ~1700 events in the 2026-08-19T22:00Z hour) left no row in the
 * telemetry table and were closed as unattributable four times over.
 */
export type { ProcessorDropInfo, ProcessorDropHandler };
export type { SinkArmedInfo, SinkArmedHandler };

// Cap telemetry per drop key per pageload so a looping client can't turn a
// swallowed error burst into a PostHog event burst. 20 is enough to make a
// burst unmistakable across sessions while bounding a single pathological tab.
//
// The key is `reason|pattern`, not `pattern` alone (BAL-953). Keying on pattern
// would give all nine reason-only branches ONE shared 20-row budget under the
// empty pattern, so a preview-env burst would exhaust the budget for a
// localhost burst on the same pageload and hide it — reintroducing the blind
// spot one level down. Per-pattern behaviour for the ignore-list is unchanged:
// its key is `ignore-pattern|<regex source>`.
const MAX_DROP_REPORTS_PER_PATTERN = 20;

function isExternalScriptError(event: ErrorEvent): boolean {
  const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];
  return frames.some((frame) =>
    EXTERNAL_SCRIPT_PATHS.some((path) => frame?.filename?.includes(path)),
  );
}

/**
 * @name initializeSentryBrowserClient
 * @description Initialize the Sentry client
 * @param props
 */
export function initializeSentryBrowserClient(
  props: Parameters<typeof init>[0] & {
    onIgnoredDrop?: IgnoredDropHandler;
    onProcessorDrop?: ProcessorDropHandler;
    onSinkArmed?: SinkArmedHandler;
  } = {},
) {
  // Extract beforeSend and integrations from props to prevent them from
  // overriding ours via spread. A caller's beforeSend is chained after our
  // filters; a caller's integrations are merged after replayIntegration.
  const {
    beforeSend: callerBeforeSend,
    integrations: callerIntegrations,
    onIgnoredDrop,
    onProcessorDrop,
    onSinkArmed,
    ...restProps
  } = props ?? {};

  const resolvedEnvironment = resolveEnvironment();

  // Per-pageload throttle state for onIgnoredDrop (BAL-856).
  const dropReportCounts = new Map<string, number>();

  // Re-entrancy guard. `onIgnoredDrop` runs a PostHog capture from inside
  // `beforeSend`; if that capture ever `console.error`s, `captureConsoleIntegration`
  // (registered with `levels: ['error', 'warn']`) turns it into a new Sentry
  // event that re-enters `beforeSend`. The per-key cap already terminates that
  // cycle at 20, so this is not load-bearing — but it stops the cycle outright
  // instead of budgeting for it, and it matches the guard the sibling sink in
  // `processor-drop-telemetry.ts` already carries.
  let reporting = false;

  /**
   * Attribute one `beforeSend` drop. Called on EVERY `return null` below —
   * that completeness is the whole point of BAL-953, because Sentry's
   * `client_discard / before_send` counter counts every one of them and a
   * telemetry table covering a subset can never reconcile against it.
   *
   * There is no pageload-wide ceiling here, unlike the processor sink, and that
   * is deliberate. Worst case is 20 rows × the number of live keys, and the
   * keys are a fixed set (one per ignore pattern, plus one per reason) rather
   * than the unbounded per-message signatures the processor sink faces. A total
   * ceiling would buy a ~20% volume reduction by silently truncating whichever
   * attribution arrived last — which is the failure this sink exists to remove.
   */
  function reportIgnoredDrop(args: {
    reason: DropReason;
    errorValue: string;
    pattern?: string;
  }) {
    if (!onIgnoredDrop) return;
    if (reporting) return;
    const { reason, errorValue, pattern = '' } = args;
    const key = `${reason}|${pattern}`;
    const count = dropReportCounts.get(key) ?? 0;
    if (count >= MAX_DROP_REPORTS_PER_PATTERN) return;
    dropReportCounts.set(key, count + 1);
    reporting = true;
    try {
      onIgnoredDrop(
        buildDropInfo({
          reason,
          pattern,
          errorValue,
          // ONE place decides whether the text ships, and it keys on the
          // ORIGIN rather than on which branch fired. Keying on the reason
          // instead would have covered only the `localhost` branch — and that
          // branch tests two exact hostnames, so a dev machine on `0.0.0.0`,
          // `::1`, or a LAN address falls through to a later reason and would
          // have shipped its text. Found by the BAL-953 security review.
          // `undefined` when `window` is absent — this module is pulled into
          // the SERVER graph via the @kit/sentry barrel. That correctly reads
          // as production in a production build; under `next dev` the build
          // signal inside `isLocalOrigin` covers it.
          suppressMessage: isLocalOrigin(
            typeof window !== 'undefined'
              ? window.location.hostname
              : undefined,
          ),
          route:
            typeof window !== 'undefined'
              ? normalizeRoutePath(window.location.pathname)
              : 'unknown',
          environment: resolvedEnvironment,
        }),
      );
    } catch {
      // Telemetry must never break the drop path.
    } finally {
      reporting = false;
    }
  }

  /**
   * Discard the event, attributed. The ONLY way `beforeSend` returns null.
   *
   * Dropping and attributing the drop are one statement here, so they cannot
   * come apart — which is the actual BAL-953 fix. BAL-856 and BAL-891 each
   * added a `reportIgnoredDrop` call NEXT TO one `return null` and left the
   * rest untouched; nothing failed, because a silent drop
   * looks exactly like an event that never happened. Convention had two
   * chances and lost both. `drop-reason-coverage.test.ts` fails the build on a
   * bare `return null` inside `beforeSend`.
   */
  function dropEvent(args: {
    reason: DropReason;
    errorValue: string;
    pattern?: string;
  }): null {
    reportIgnoredDrop(args);
    return null;
  }

  const client = init({
    dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,

    // Derive Sentry environment from VERCEL_ENV so preview deployments don't
    // pollute the production signal. Caller can still override via restProps.
    environment: resolvedEnvironment,

    // Use tunnel to bypass ad blockers that block requests to sentry.io
    // @see https://docs.sentry.io/platforms/javascript/troubleshooting/#using-the-tunnel-option
    tunnel: '/api/monitoring/sentry',

    // Replay may only be enabled for the client-side. Without
    // replayIntegration() registered here, replaysSessionSampleRate /
    // replaysOnErrorSampleRate below are dead config — Sentry records
    // nothing (0 replays org-wide while BALLEE-E3 sat unreproducible).
    // Merge caller integrations WITH SDK defaults instead of letting the
    // restProps spread replace them. NOTE: replayIntegration() cannot live
    // here — this module is also pulled into the server graph via the
    // @kit/sentry barrel, and @sentry/nextjs's node export has no
    // replayIntegration (Turbopack hard-fails the build). It is registered
    // in apps/web/instrumentation-client.ts, which is browser-graph-only.
    // Without it there, replaysSessionSampleRate / replaysOnErrorSampleRate
    // below are dead config and Sentry records no replays at all.
    integrations: (defaults) =>
      typeof callerIntegrations === 'function'
        ? callerIntegrations(defaults)
        : [...defaults, ...(callerIntegrations ?? [])],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: restProps?.tracesSampleRate ?? 1.0,

    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // Note: if you want to override the automatic release value, do not set a
    // `release` value here - use the environment variable `SENTRY_RELEASE`, so
    // that it will also get attached to your source maps,
    ...restProps,

    // beforeSend AFTER the spread so it's never overridden
    beforeSend(event: ErrorEvent, hint: EventHint) {
      // Raw text for the drop branches that fire before any message matching.
      // Lazy on purpose, and it must stay lazy: the branches after the
      // console-message recovery below need the RECOVERED text, which is
      // written back onto `event.message` mid-function. Snapshotting up front
      // would report `[object Object]` on every drop after that point.
      // `buildDropInfo` redacts what this returns — nothing raw reaches the
      // sink.
      const dropText = () =>
        resolveEventText({
          message: event.message,
          originalError: hint.originalException,
          exceptionValue: event.exception?.values?.[0]?.value,
        });

      // Drop everything from Vercel preview deployments (BAL-791). Previews
      // point at the throwaway e2e Supabase branch and report its rebuild
      // artifacts (42501 permission-denied, BALLEE-GK/GJ) as if they were
      // product defects. Deliberate tradeoff: preview-only crashes no longer
      // reach Sentry. Production/staging are untouched.
      //
      // Reported (BAL-953). The `event_processor` sink deliberately does NOT
      // report preview/localhost drops — see `shouldReport` in
      // `processor-drop-telemetry.ts` — and that asymmetry is intentional
      // rather than an oversight. This half exists to reconcile against
      // Sentry's `before_send` discard counter, which counts these; excluding
      // them would guarantee the two never add up and would leave the
      // localhost-tagged-production path, a named burst candidate, silent.
      if (isPreviewEnvironment(resolvedEnvironment)) {
        return dropEvent({
          reason: DROP_REASONS.previewEnvironment,
          errorValue: dropText(),
        });
      }

      // Drop events from local dev. Misconfigured local environments stamp
      // `production` on the environment tag while serving from localhost:3012,
      // which floods the production project with noise.
      //
      // The message text does not ship from here — but that is decided in
      // `reportIgnoredDrop` by the ORIGIN, not by this branch, so a dev machine
      // that misses these two exact hostnames is covered too. See the
      // `suppressMessage` note there.
      //
      // This condition stays EXACTLY as it was: it decides what Sentry drops,
      // not what telemetry ships, and widening it would silence events that
      // currently reach Sentry.
      if (
        typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' ||
          window.location.hostname === '127.0.0.1')
      ) {
        return dropEvent({
          reason: DROP_REASONS.localhost,
          errorValue: dropText(),
        });
      }

      const originalError = hint.originalException;
      const eventExceptionValue =
        event.exception?.values?.[0]?.value?.toLowerCase() || '';

      // Recover the real text from structured-log console captures BEFORE any
      // message matching runs, so `rawMsg` below, the ignore list, and Sentry's
      // own inbound filters all see it instead of `[object Object]` (BALLEE-GZ).
      const recoveredConsoleMessage = recoverConsoleObjectMessage(event);
      if (recoveredConsoleMessage) {
        event.message = recoveredConsoleMessage;
      }

      // Cloudflare-origin-timeout fingerprint. Mirror of the server-side
      // filter — when Supabase Postgres is unreachable, REST calls return
      // an HTML 5xx body from Cloudflare (522/523/524) that surfaces in
      // the browser as a fetch parse failure. Group all such events under
      // one fingerprint so a single outage doesn't produce hundreds of
      // unrelated-looking issues. See `sentry.server.config.ts` for the
      // canonical comment block.
      const rawMsg = String(
        event.message ??
          (originalError instanceof Error
            ? originalError.message
            : typeof originalError === 'string'
              ? originalError
              : (event.exception?.values?.[0]?.value ?? '')),
      );
      if (
        rawMsg.includes('<!DOCTYPE html>') ||
        /\b52[234]\b/.test(rawMsg) ||
        rawMsg.startsWith('<html')
      ) {
        event.fingerprint = ['cloudflare-origin-timeout'];
        event.tags = {
          ...(event.tags ?? {}),
          upstream_class: 'cloudflare-522',
          outage_signal: 'true',
        };
        event.level = 'error';
        if (event.message && event.message.length > 200) {
          event.message =
            event.message.slice(0, 200) + '... [HTML body truncated]';
        }
        const exceptionValue = event.exception?.values?.[0]?.value;
        if (exceptionValue && exceptionValue.length > 200) {
          event.exception!.values![0]!.value =
            exceptionValue.slice(0, 200) + '... [HTML body truncated]';
        }
        return event;
      }

      // Drop "missing NEXT_PUBLIC_SUPABASE_*" ZodErrors from non-production
      // environments. Vercel preview deploys without these env vars crash
      // every page render — capturing one is fine, capturing 100s/hour
      // pollutes the prod signal. See BAL-169.
      if (
        resolvedEnvironment !== 'production' &&
        isMissingSupabaseKeysError(originalError, eventExceptionValue)
      ) {
        return dropEvent({
          reason: DROP_REASONS.missingSupabaseKeys,
          errorValue: dropText(),
        });
      }

      // Drop the downstream TypeError ("Cannot set property message of
      // [object Object] which has only a getter") thrown when Next.js's
      // app-page runtime tries to mutate the frozen ZodError above.
      // Same root cause as BAL-169 but surfaces as a different Sentry
      // issue (BAL-171 / BALLEE-BV).
      if (
        resolvedEnvironment !== 'production' &&
        isPreviewSupabaseKeysTypeError(event, eventExceptionValue)
      ) {
        return dropEvent({
          reason: DROP_REASONS.supabaseKeysTypeError,
          errorValue: dropText(),
        });
      }

      // Check for expected validation errors marked by code
      if (
        originalError &&
        typeof originalError === 'object' &&
        'isExpectedValidation' in originalError &&
        (originalError as { isExpectedValidation?: boolean })
          .isExpectedValidation
      ) {
        return dropEvent({
          reason: DROP_REASONS.expectedValidation,
          errorValue: dropText(),
        });
      }

      // Drop errors already captured at the service boundary. Mirror of the
      // server-side filter; see sentry.server.config.ts for the why.
      if (
        originalError &&
        typeof originalError === 'object' &&
        'alreadyCapturedToSentry' in originalError &&
        (originalError as { alreadyCapturedToSentry?: boolean })
          .alreadyCapturedToSentry
      ) {
        return dropEvent({
          reason: DROP_REASONS.alreadyCaptured,
          errorValue: dropText(),
        });
      }

      // Filter ValidationError by name
      if (
        originalError instanceof Error &&
        originalError.name === 'ValidationError'
      ) {
        return dropEvent({
          reason: DROP_REASONS.validationErrorName,
          errorValue: dropText(),
        });
      }

      // Filter GoTrue's AuthWeakPasswordError by name. This is an EXPECTED
      // user-input validation outcome on /auth/sign-up (the user typed a
      // password below the policy minimum) — never a defect. The sign-up UI
      // surfaces it via AuthErrorAlert; it only reaches Sentry because the
      // sign-up flow re-throws it, producing an unhandled rejection. Matching
      // by name (auth-js sets it explicitly, so minification-safe) suppresses
      // it regardless of the length threshold or message wording.
      // See BALLEE-F7 / BALLEE-F9 / BAL-576.
      if (
        originalError instanceof Error &&
        originalError.name === 'AuthWeakPasswordError'
      ) {
        return dropEvent({
          reason: DROP_REASONS.weakPasswordErrorName,
          errorValue: dropText(),
        });
      }

      // Drop the session recorder's own cross-origin stylesheet warning
      // (BALLEE-HG). `pattern` carries the regex this branch matched on, which
      // is its own rather than an IGNORED_ERROR_PATTERNS entry — see that
      // guard's docblock for why no such entry can reach this event shape.
      //
      // This is the one drop site that does not call dropText(). The two are
      // indistinguishable here — the guard above requires event.message to be
      // a non-empty string, and resolveEventText returns exactly that first —
      // so this spelling is kept because it is dev's, not because it differs.
      // Don't "fix" it for uniformity; the equivalence is the whole point.
      if (isRrwebCssSerializerWarning(event)) {
        return dropEvent({
          reason: DROP_REASONS.rrwebCssWarning,
          pattern: RRWEB_CSS_SERIALIZER_WARNING.source,
          errorValue: String(event.message ?? ''),
        });
      }

      // Filter out transient/ignored errors by message.
      //
      // The recovered console message wins when present: for a console capture
      // `hint.originalException` is the SAME String()-cast text as
      // `event.message` (`[object Object]`), which matches no pattern by
      // construction. Preferring the recovered text is what makes this class
      // filterable at all; every other path is unchanged.
      const errorMessage =
        recoveredConsoleMessage ??
        (originalError instanceof Error
          ? originalError.message
          : typeof originalError === 'string'
            ? originalError
            : '');

      if (errorMessage) {
        const matched = findIgnoredPattern(errorMessage);
        if (matched) {
          return dropEvent({
            reason: DROP_REASONS.ignorePattern,
            pattern: matched,
            errorValue: errorMessage,
          });
        }
      }

      // Also check event exception values
      if (eventExceptionValue) {
        const matched = findIgnoredPattern(eventExceptionValue);
        if (matched) {
          return dropEvent({
            reason: DROP_REASONS.ignorePattern,
            pattern: matched,
            // Report the ORIGINAL-case value, not the lowercased copy used for
            // matching. The server half fixed this under BAL-891 and the
            // browser was left behind: lowercasing fragments grouping in
            // PostHog against the other call site, and it silently defeats the
            // one case-sensitive redaction rule — `/(AKIA|ASIA)[A-Z0-9]{12,}/`
            // cannot match a lowercased AWS key id, so it would ship intact.
            errorValue:
              event.exception?.values?.[0]?.value ?? eventExceptionValue,
          });
        }
      }

      // Filter out errors from third-party scripts (browser extensions, widgets)
      if (isExternalScriptError(event)) {
        return dropEvent({
          reason: DROP_REASONS.externalScript,
          errorValue: dropText(),
        });
      }

      // Chain caller's beforeSend if provided
      if (callerBeforeSend) {
        return callerBeforeSend(event, hint);
      }

      return event;
    },
  });

  // BAL-959: attribute drops made by an EVENT PROCESSOR, which happen strictly
  // before `beforeSend` runs and are therefore invisible to every filter and
  // hook above. Installed after `init` because the SDK registers each
  // integration's `processEvent` as a client event processor during `init`
  // itself, so the list to wrap does not exist until this point.
  //
  // Additive only: each wrapper delegates to the original processor and
  // returns its result untouched. Nothing about what Sentry captures, drops or
  // dedupes changes — which matters, because changing capture behaviour on the
  // strength of an unconfirmed hypothesis would destroy the signal this exists
  // to record.
  if (onProcessorDrop) {
    instrumentEventProcessorDrops({
      client,
      environment: resolvedEnvironment,
      onProcessorDrop,
      onSinkArmed,
      // The client's processor list is only HALF the chain. `prepareEvent`
      // appends the merged global + isolation + current scope processors after
      // it, and a falsy return from any of them is billed `event_processor`
      // identically. @sentry/nextjs's own `filterNextRedirectError` — which
      // drops every `NEXT_REDIRECT` — is registered by `core.addEventProcessor`
      // onto the ISOLATION scope from inside the `init` call above, so it is
      // already present here and would have been invisible to a client-only
      // wrap. Passed as resolvers, not scopes: the current scope is a fresh
      // clone inside every `withScope`.
      scopes: [
        { kind: 'global', resolve: getGlobalScope },
        { kind: 'isolation', resolve: getIsolationScope },
        { kind: 'current', resolve: getCurrentScope },
      ],
    });
  }

  return client;
}
